/**
 * Søknader as an action queue (owner, 2026-09-21): what needs an answer, what is coming, and
 * what is done. Pure, so the three lists and what a request collides with are tested without
 * a screen.
 */
import type { ApiAbsence, ApiShift, ApiWorkTimeChange } from '@/api/data';
import { oslo } from '@/rules';
import { inRange } from '@timefly/core';
import type { Kind } from './AbsenceRange';
import { spanOf } from './dates';

export type Bucket = 'handling' | 'kommende' | 'historikk';
export const BUCKETS: [Bucket, string][] = [
  ['handling', 'Krever handling'],
  ['kommende', 'Kommende'],
  ['historikk', 'Historikk'],
];

/**
 * Which list a request belongs in. Pending needs an answer. An approved absence the employee has
 * asked to cancel needs one too, until it is over. Approved and not started is coming; the rest
 * (declined, started, over) is history.
 */
export function bucketOf(
  a: Pick<ApiAbsence, 'status' | 'start_date' | 'end_date'>,
  today: string,
  cancelAsked = false,
): Bucket {
  const { from, to } = spanOf(a);
  if (a.status === 'pending') return 'handling';
  if (a.status === 'accepted' && cancelAsked && to >= today) return 'handling';
  if (a.status === 'accepted' && from > today) return 'kommende';
  return 'historikk';
}

/** One list, in the order it is worked: the queue and what is coming soonest first, history newest first. */
export function queueOf<A extends Pick<ApiAbsence, 'id' | 'status' | 'start_date' | 'end_date'>>(
  all: A[],
  bucket: Bucket,
  today: string,
  cancelIds: ReadonlySet<number> = new Set(),
): A[] {
  const list = all.filter((a) => bucketOf(a, today, cancelIds.has(a.id)) === bucket);
  return list.sort((a, b) =>
    bucket === 'historikk'
      ? b.start_date.localeCompare(a.start_date)
      : a.start_date.localeCompare(b.start_date),
  );
}

/**
 * The person's published shifts inside a request. A full day is matched by date; a clock range by
 * instant, so a 10:00 to 12:00 request names the shift it cuts into and not every shift that day.
 * The drawer reads a draft the same way.
 */
export function conflictsOf<S extends Pick<ApiShift, 'user_id' | 'published' | 'start_time' | 'end_time'>>(
  a: Pick<ApiAbsence, 'user_id' | 'start_date' | 'end_date'>,
  kind: Kind,
  shifts: S[],
): S[] {
  const own = shifts.filter((s) => s.user_id === a.user_id && s.published);
  if (kind === 'date') {
    const { from, to } = spanOf(a);
    return own.filter((s) => inRange(oslo(s.start_time)?.date ?? '', from, to));
  }
  const s0 = Date.parse(a.start_date);
  const e0 = Date.parse(a.end_date);
  return own.filter((s) => Date.parse(s.start_time) < e0 && Date.parse(s.end_time) > s0);
}

/**
 * The approved absences the employee has asked to cancel and nobody has answered: an
 * 'absence_cancel_requested' history row newer than the absence's last write. A decision or an
 * edit writes the row (updated_at); the employee's request does not, so a request older than the
 * last write has been dealt with, and one on an absence no longer approved is moot.
 */
export function cancelIdsOf(
  rows: Pick<ApiWorkTimeChange, 'absence_id' | 'action' | 'created_at'>[],
  absences: Pick<ApiAbsence, 'id' | 'status' | 'updated_at'>[],
): Set<number> {
  const out = new Set<number>();
  for (const r of rows) {
    if (r.action !== 'absence_cancel_requested' || r.absence_id == null) continue;
    const a = absences.find((x) => x.id === r.absence_id);
    if (a?.status === 'accepted' && (!a.updated_at || r.created_at >= a.updated_at)) out.add(a.id);
  }
  return out;
}

/**
 * The orders a list can be read in (Fravær V1.4, #276), by list. The first is the default. The
 * address carries the choice (`?sorter=`), and a choice the list does not offer reads as its default.
 */
export type Sort = 'snart' | 'eldste' | 'avklares' | 'nyeste';
export const SORTS: Record<Bucket, [Sort, string][]> = {
  handling: [
    ['snart', 'Starter snart'],
    ['eldste', 'Eldste søknad'],
    ['avklares', 'Må avklares først'],
  ],
  kommende: [
    ['snart', 'Tidligste periode'],
    ['eldste', 'Eldste søknad'],
  ],
  historikk: [
    ['nyeste', 'Nyeste periode'],
    ['eldste', 'Eldste søknad'],
  ],
};
export const sortFor = (bucket: Bucket, asked: string | null): Sort =>
  (SORTS[bucket].find(([k]) => k === asked) ?? SORTS[bucket][0])?.[0] ?? 'snart';

/**
 * The whole list in the chosen order, a new array (the cached one is never sorted in place). Every
 * order ends on the id, so two requests alike in everything else keep one order.
 * - snart: what has not ended first, by start; a request whose days are over comes after, also by start
 * - eldste: by when the request was sent
 * - avklares: the requests in `first` (the Må avklares subset, its exact predicate), then as snart
 * - nyeste: the latest period first
 */
export function sortQueue<A extends Pick<ApiAbsence, 'id' | 'start_date' | 'end_date' | 'created_at'>>(
  list: A[],
  sort: Sort,
  today: string,
  first: ReadonlySet<number> = new Set(),
): A[] {
  const over = (a: A) => (spanOf(a).to < today ? 1 : 0);
  const soon = (a: A, b: A) => over(a) - over(b) || a.start_date.localeCompare(b.start_date) || a.id - b.id;
  const cmp =
    sort === 'eldste'
      ? (a: A, b: A) => (a.created_at ?? '').localeCompare(b.created_at ?? '') || a.id - b.id
      : sort === 'nyeste'
        ? (a: A, b: A) => b.start_date.localeCompare(a.start_date) || b.id - a.id
        : sort === 'avklares'
          ? (a: A, b: A) => (first.has(a.id) ? 0 : 1) - (first.has(b.id) ? 0 : 1) || soon(a, b)
          : soon;
  return [...list].sort(cmp);
}

/**
 * What the Saldo column says about one request (#276). Every number is the server's: the balance
 * from GET /admin/absence-standing, the amount from GET /absence-requests/deduction; the only sum
 * here is the one the approve route makes, balance minus amount, refused below 0 (assertBalanceCovers).
 * - none: the type takes no vacation days («Ingen ferietrekk»)
 * - hidden: the reader may not read balances (requests.decide)
 * - unknown: the read failed, or the year has no balance on the server; never shown as 0
 * - split: the request is over new year; the drawer shows the years apart
 * - older: a holiday year before this one, whose balance is gone
 * - now: today's balance for the year (approved and earlier requests: not the balance back then)
 * - after: a pending request, the balance now and after approving this one alone
 */
export type RowBalance =
  | { kind: 'none' | 'hidden' | 'loading' | 'unknown' | 'split' | 'older' }
  | { kind: 'now'; now: number }
  | { kind: 'after'; now: number; take: number; after: number };

type Read<T> = { state: 'loading' | 'error' | 'ok' | 'off'; value?: T | undefined };

export function balanceOf(o: {
  pending: boolean;
  deductible: boolean;
  from: string;
  to: string;
  today: string;
  standing: Read<number>;
  charge: Read<number>;
}): RowBalance {
  if (!o.deductible) return { kind: 'none' };
  if (o.from.slice(0, 4) !== o.to.slice(0, 4)) return { kind: 'split' };
  const year = Number(o.from.slice(0, 4));
  const thisYear = Number(o.today.slice(0, 4));
  if (year < thisYear) return { kind: 'older' };
  if (year > thisYear + 1) return { kind: 'unknown' };
  if (o.standing.state === 'off') return { kind: 'hidden' };
  if (o.standing.state === 'loading') return { kind: 'loading' };
  if (o.standing.state === 'error' || o.standing.value == null) return { kind: 'unknown' };
  const now = o.standing.value;
  if (!o.pending) return { kind: 'now', now };
  if (o.charge.state === 'loading') return { kind: 'loading' };
  if (o.charge.state !== 'ok' || o.charge.value == null) return { kind: 'unknown' };
  return { kind: 'after', now, take: o.charge.value, after: now - o.charge.value };
}

/** The Må avklares subset: a pending request that cannot be approved as it stands. */
export const needsClarification = (
  a: Pick<ApiAbsence, 'status'>,
  typeGone: boolean,
  b: RowBalance | undefined,
) => a.status === 'pending' && (typeGone || b?.kind === 'unknown' || (b?.kind === 'after' && b.after < 0));

/** The people with approved absence on any day from `from` to `to`, both included. */
export function peopleAway(
  all: Pick<ApiAbsence, 'user_id' | 'status' | 'start_date' | 'end_date'>[],
  from: string,
  to: string,
) {
  const ids = new Set<number>();
  for (const a of all) {
    if (a.status !== 'accepted') continue;
    const s = spanOf(a);
    if (s.from <= to && s.to >= from) ids.add(a.user_id);
  }
  return ids;
}
